import React, { Fragment, useCallback, useId, useMemo, useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { groupBy } from 'lodash-es';
import InfiniteScroll from 'react-infinite-scroll-component';

import {
  Menu,
  MenuItem,
  MenuGroup,
  MenuButton,
  MenuHeader,
  MenuDivider,
  useMenuProps,
  useShadowLine,
  useSearchOnMenuOpen,
  filterBySearchQuery,
} from '../../../../../components/DropdownSet';
import SearchField from '../../../../../components/Search/SearchField';
import LoadingSpinner from '../../../../../components/LoadingSpinner';
import Tooltip from '../../../../../components/Tooltip';

const emptyArray = [];
const infiniteScrollStyles = { overflow: 'visible' };

const CompanyAppsDropdown = ({
  selected,
  options = emptyArray,
  unselectedStateText = 'Select App',
  disabled = false,
  groupAppsBy,
  idKey = 'ref_no',
  hideSearch = false,
  footer = null,

  // Manual search props
  manualSearch = false,
  pending,
  pagination,
  onSearchChange,
  onLoadMore,
  onChange,
}) => {
  const componentId = useId();
  const scrollId = `company-apps-menu-group-${componentId}`;

  const menuProps = useMenuProps({
    menuStyle: { width: 300, paddingBottom: footer ? 64 : 12 },
    className: 'company-apps-dropdown-menu',
  });
  const [shadowLine, onScroll] = useShadowLine();
  const [searchQuery, setSearchQuery] = useState('');

  const { searchInstanceRef, onMenuChange: onSearchMenuChange } = useSearchOnMenuOpen();

  const menuOptions = manualSearch ? options : filterBySearchQuery(options, searchQuery, ['query']);

  const queryPresent = searchInstanceRef.current?.getSearchQuery()?.length > 0;

  const noResults = queryPresent && !menuOptions.length;

  // Selected option
  const selectedOption = useMemo(
    () => options.find((option) => option.value[idKey] === selected?.[idKey]) || {},
    [selected, idKey]
  );

  const handleSearchChange = useCallback(
    (value) => {
      if (manualSearch) onSearchChange(value);
      else setSearchQuery(value);
    },
    [manualSearch, onSearchChange]
  );

  const handleLoadMore = useCallback(() => {
    if (manualSearch) onLoadMore(pagination.offset + pagination.limit);
  }, [manualSearch, onLoadMore, pagination]);

  const handleChange = useCallback(
    ({ value, name }) => {
      onChange(value, name);
    },
    [onChange]
  );

  const handleMenuChange = ({ open }) => {
    // Cleanup search field on toggle Menu
    if (!hideSearch) onSearchMenuChange({ open });
  };

  const renderMenuOptions = () => {
    const renderMenuOption = (option, index) => {
      const { label, value, disabled: isDisabled = false, tooltip, query } = option;
      const item = (
        <MenuItem
          key={value[idKey] || index}
          value={value}
          disabled={isDisabled}
          className={cx('menu-item--14', {
            active: selectedOption.value?.[idKey] === value[idKey],
          })}
          onClick={(v) => handleChange({ ...v, name: query })}
        >
          {label}
        </MenuItem>
      );

      if (isDisabled && tooltip) {
        return (
          <Tooltip
            key={value[idKey] || index}
            content={tooltip}
            placement="right"
            colorVariant="light"
            wrapChildrenInHtmlNode
          >
            <div className="menu-item-tooltip-wrapper">{item}</div>
          </Tooltip>
        );
      }

      return item;
    };

    if (groupAppsBy) {
      const groupedOptions = groupBy(menuOptions, groupAppsBy);
      const groupLabels = Object.keys(groupedOptions);

      return groupLabels.reduce((acc, groupLabel, index) => {
        const isLast = index === groupLabels.length - 1;

        return acc.concat(
          <Fragment key={groupLabel}>
            <MenuHeader>{groupLabel}</MenuHeader>
            {groupedOptions[groupLabel].map(renderMenuOption)}
            {!isLast ? <MenuDivider fullWidth dense /> : null}
          </Fragment>
        );
      }, []);
    }

    return menuOptions.map(renderMenuOption);
  };

  const hasMoreApps = manualSearch && pagination?.total > options.length;

  return (
    <Menu
      menuButton={
        <MenuButton disabled={disabled}>
          {selectedOption.buttonLabel || selectedOption.label || unselectedStateText}
        </MenuButton>
      }
      setDownOverflow
      {...menuProps}
      onMenuChange={handleMenuChange}
    >
      {!hideSearch && <SearchField instanceRef={searchInstanceRef} onChange={handleSearchChange} />}
      {!hideSearch && <MenuDivider key="divider" fullWidth dense />}
      {shadowLine}
      <MenuGroup takeOverflow id={scrollId} onScroll={onScroll}>
        {manualSearch ? (
          <InfiniteScroll
            dataLength={options.length}
            hasMore={!pending && hasMoreApps}
            scrollThreshold={0.95}
            scrollableTarget={scrollId}
            style={infiniteScrollStyles}
            className="company_apps__list pos-rel"
            next={handleLoadMore}
          >
            {renderMenuOptions()}
            {pending && (
              <LoadingSpinner
                size="sm"
                id="company-apps-more"
                className="text-blue justify-content-center"
              />
            )}
          </InfiniteScroll>
        ) : (
          <div>{renderMenuOptions()}</div>
        )}
        {noResults && <div className="no-results-state">No matching results</div>}
      </MenuGroup>
      {footer && <div className="company-apps-dropdown-footer">{footer}</div>}
    </Menu>
  );
};

CompanyAppsDropdown.propTypes = {
  selected: T.object,
  options: T.array,
  unselectedStateText: T.string,
  disabled: T.bool,
  groupAppsBy: T.oneOfType([T.string, T.func]),
  idKey: T.string,
  hideSearch: T.bool,
  footer: T.node,
  manualSearch: T.bool,
  pending: T.bool,
  pagination: T.object,
  onSearchChange: T.func,
  onLoadMore: T.func,
  onChange: T.func,
};

export default CompanyAppsDropdown;
